import React, { HTMLAttributes, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";

//import store from "store";
import styles from "./index.module.scss";
import Sort from "./Sort";
import XtPane from "../Pane";
import { Checkbox, List } from "antd";
import Pagination, { PaginationProps } from "../Pagination/Simple";
import type { CustomAxiosRequestConfig, PromiseRes } from "@xt/xtbase";
import VirtualList from "rc-virtual-list";

import type { SortStates } from "./Sort";
import { deepClone } from "@/utils/method";
import axios from "utils/axios";
import store from "@/store";
import OverflowTip, { OverflowTipProps } from "../OverflowTip";
export interface ColumnType<RecordType> {
  label?: React.ReactNode;
  prop?: keyof RecordType;
  /**列宽flex占比 */
  flexWeight?: number;
  textAlign?: "left" | "center" | "right";
  width?: string;
  /**body单元格超出是否省略号并且toolTip */
  showOverflowTip?: boolean;
  /**自定义单元格body样式 */
  bodyCellstyle?: StyleProps;
  headerCellStyle?: StyleProps;
  isSort?: boolean;
  isI18n?: boolean;
  isUpperCase?: boolean;
  /** 格式化处理当前值 */
  formatter?(text: any, record: RecordType, prop: keyof RecordType, col: ColumnType<RecordType>): any;
  /**手动渲染 */
  render?(text: any, record: RecordType, index: number, OverflowTip: React.FC<OverflowTipProps>): React.ReactNode;
  /**排序函数 */
  sorter?(l: RecordType, r: RecordType, sortDirection: SortStates): number;
}

export type ColumnsType<RecordType = unknown> = ColumnType<RecordType>[];
interface RowNeedProps extends ObjAny {
  _isChecked: boolean;
  id: string;
  _isExpanded: boolean;
}
export interface XtTableRefProps {
  initPageQuery(clearData?: boolean): void;
}
export interface XtTableProps<RecordType extends ObjAny> extends HTMLAttributes<HTMLDivElement> {
  ref?: CompRef<XtTableRefProps>;
  attr?: any;
  /**是否开启虚拟列表 */
  virtual?: boolean;
  itemKey?: string | ((item: RecordType) => string);
  /**body单元格超出是否省略号并且toolTip */
  showOverflowTip?: boolean;
  /**是否需要分页 */
  isPagination?: boolean;
  /**是否根据id进行分页 */
  isPaginationById?: boolean;
  /**只有一页数据是否隐藏分也 */
  hiddenSinglePagination?: boolean;
  /**表格列参数 */
  columns: ColumnsType<RecordType>;
  /**最小高度 */
  minHeight?: number;
  /**是否展示行箭头 */
  isRowArrow?: boolean;
  /**是否展开 */
  isExpandable?: boolean;
  /**是否可复选， 配合checkChange方法使用 */
  isCheckable?: boolean;
  /**是否面板table */
  isPaneTable?: boolean;
  /**是否调整布局，仅列表内容部分滚动； 注：table父容器必须固定高度 */
  isFixLayout?: boolean;
  /**是否需要登录模块 */
  isNeedLogin?: boolean;
  /**主题, Light/Dark */
  theme?: "Light" | "Dark";
  /**是否展示loading */
  isShowLoading?: boolean;
  /**暂无数据是否展示icon */
  isShowEmptyImg?: boolean;
  minWidth?: string;
  /**表格头部行的背景色 */
  headerRowBgColor?: string;
  /**表格头部行的字体颜色 */
  headerRowColor?: string;
  /**表格身体行的背景色 */
  bodyRowBgColor?: string;
  /**表格身体行的字体颜色 */
  bodyRowColor?: string;
  /** 表格行悬浮背景色 */
  rowHoverColor?: string;
  overflowType?: StyleProps["overflowY"];
  /**是否后台排序 */
  sortFetch?: boolean;
  /**分页每页数量*/
  pageSize?: number;
  /**对请求参数和返回结果的拦截处理 */
  interceptors?: {
    request?(params: CustomAxiosRequestConfig["params"]): CustomAxiosRequestConfig["params"];
    response?(res: any): any;
  };
  /**请求接口函数 */
  fetchMethod(config?: CustomAxiosRequestConfig): PromiseRes;
  checkChange?(checkedData: (RecordType & RowNeedProps)[], tableData: (RecordType & RowNeedProps)[]): void;
  queryComponent?: React.ReactNode;
  panneComponent?: React.ReactNode;
  expandContainer?(record: RecordType): React.ReactNode;
}

const PAGE_NEXT = "NEXT";
const PAGE_PRE = "PREV";
const direction = "NEXT";
const { useTranslation } = Hooks;
const { Big } = Util;
const XtPagination: React.FC<PaginationProps> = Pagination;

const Table = (
  {
    className,
    virtual = false,
    itemKey,
    isPagination = true,
    showOverflowTip = false,
    isPaginationById = false,
    hiddenSinglePagination = false,
    columns = [],
    minHeight = 400,
    isRowArrow = false,
    isExpandable = false,
    isCheckable = false,
    isPaneTable = false,
    isFixLayout = false,
    isNeedLogin = true,
    theme = "Light",
    isShowLoading = true,
    isShowEmptyImg = true,
    minWidth = "fit-content",
    overflowType = "auto",
    headerRowColor,
    headerRowBgColor,
    bodyRowBgColor,
    bodyRowColor,
    rowHoverColor,
    sortFetch = true,
    pageSize = 10,
    interceptors = {},
    fetchMethod,
    checkChange,
    queryComponent,
    panneComponent,
    expandContainer,
  }: XtTableProps<any>,
  ref: XtTableProps<any>["ref"]
) => {
  const t = useTranslation();
  //const {isLogin} = store.user;
  // useRef
  const paginationRef = useRef<{ enablePre: () => any; enableNext: (flag?: boolean) => any }>(null);
  const TableContainerRef = useRef<HTMLDivElement>(null);
  const SearchViewRef = useRef<HTMLDivElement>(null);
  const TableHeaderRef = useRef<HTMLUListElement>(null);
  const PaneViewRef = useRef<HTMLDivElement>(null);
  // useState
  const [fromId, setFromId] = useState(""); // 当前页的第一条数据id（获取上一页数据）或者最后一条数据id（获取下一页数据）
  const [pageStatus, setPageStatus] = useState({ hasPrev: true, hasNext: true });
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);
  const [tableData, setTableData] = useState<RowNeedProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPagination, setShowPagination] = useState(false);
  const [tableContentHeight, setTableContentHeight] = useState(200);
  const [sortProp, setSortProp] = useState(""); // 排序字段
  const [sortDirection, setSortDirection] = useState("default" as SortStates); // 排序类型
  const [isCheckAll, setIsCheckAll] = useState(false); // 全选flag
  const [indeterminate, setIndeterminate] = useState(false); // 半选 flag

  useImperativeHandle(ref, () => ({
    initPageQuery,
  }));
  // useEffect
  useEffect(() => {
    initPageQuery();
    fixLayout();
  }, []);

  useEffect(() => {
    if (!paginationRef.current) return;
    paginationRef.current.enableNext(tableData!.length < pageSize);
  }, [tableData, showPagination]);
  // useMemo
  const pageCount = useMemo(() => {
    return Math.ceil(total / pageSize);
  }, [total]);
  const isEmpty = useMemo(() => {
    if (!Array.isArray(tableData)) return true;
    return tableData.length <= 0;
  }, [tableData]);

  const tableContentStyle = useMemo(() => {
    return { height: isFixLayout ? `${tableContentHeight}px` : "100%" };
  }, []);

  const getTableData = useMemo(() => {
    if (sortFetch) return tableData;
    const data = (deepClone(tableData) as typeof tableData) || [];
    const col = columns.find((col) => col.prop === sortProp);
    if (sortDirection === "default" || !sortDirection) return data;
    const sortData = data.sort((a, b) => {
      if (col?.sorter && typeof col.sorter === "function") {
        const { l, r } = getSortParams(a, b, sortDirection);
        const sortRes = col.sorter && col.sorter(l, r, sortDirection);
        return sortRes;
      } else {
        return defaultSorter(a, b, sortDirection);
      }
    });
    return sortData;
  }, [tableData, sortFetch, sortProp, sortDirection]);

  // method
  const getSortParams = (a: RowNeedProps, b: RowNeedProps, sortDirection: SortStates) => {
    const obj = {
      l: a,
      r: b,
    };
    if (sortDirection === "desc") {
      obj.l = b;
      obj.r = a;
    }
    return obj;
  };
  const defaultSorter = (a: RowNeedProps, b: RowNeedProps, sortDirection: SortStates) => {
    const { l, r } = getSortParams(a, b, sortDirection);
    const lStr = (l as ObjAny)[sortProp].toString();
    const rStr = (r as ObjAny)[sortProp].toString();
    const res = lStr - rStr;
    if (typeof res === "number" && res.toString() !== "NaN") {
      return Big(lStr.replaceAll(",", "")).minus(rStr).gt(0) ? 1 : -1;
    } else {
      const dateLStr = (+new Date(lStr)).toString();
      const dateRStr = (+new Date(rStr)).toString();
      if (dateLStr !== "NaN" && dateRStr !== "NaN") {
        return Big(dateLStr).minus(dateRStr).toNumber();
      } else {
        return lStr.length - rStr.length;
      }
    }
  };

  const initPageQuery = (clearData?: boolean) => {
    setPageIndex(1);
    setFromId("");
    try {
      if (clearData) setTableData([]);
      pageRecordList();
    } catch (error) {
      setTableData([]);
    }
  };

  const pageRecordList = async (type?: "NEXT" | "PREV") => {
    if (isNeedLogin && !store.user.isLogin) return [];
    setLoading(true);
    setIsCheckAll(false);
    setIndeterminate(false);
    let tempPageIndex = pageIndex;
    let fromId = "";
    if (type === PAGE_NEXT) {
      tempPageIndex = pageIndex + 1;
      if (isPaginationById) {
        fromId = tableData!.length > 0 ? tableData![tableData!.length - 1].id : "";
        setFromId(fromId);
      }
    }
    if (type === PAGE_PRE) {
      tempPageIndex = pageIndex - 1;
      if (isPaginationById) {
        fromId = tableData!.length > 0 ? tableData![0].id : "";
        setFromId(fromId);
      }
    }
    let params = {
      page: tempPageIndex,
      size: pageSize,
    } as ObjAny;

    if (isPaginationById) {
      params = {
        fromId: fromId,
        direction: type || PAGE_NEXT,
        limit: pageSize,
      };
    }
    const { list: resArr = [], hasNext, hasPrev, total } = await queryRecordList(params, type!);
    const disabledNext = isPaginationById ? !!hasNext : total !== undefined ? Math.ceil(total / pageSize) > 1 : resArr!.length < pageSize;
    if (!type) {
      // 初始化没有type 默认next  点击分页左右箭头有具体type
      // 初始化时翻页按钮置灰隐藏逻辑
      setTableData(resArr!);
      if (hiddenSinglePagination) {
        if (isPaginationById) {
          setShowPagination(hasNext || hasPrev);
        } else {
          setShowPagination(total !== undefined ? Math.ceil(total / pageSize) > 1 : resArr!.length >= pageSize);
        }
      } else {
        setShowPagination(resArr!.length > 0);
        paginationRef.current?.enableNext(disabledNext);
      }
    } else {
      setLoading(false);
      return disabledNext;
    }

    setTableData(resArr!);
    if (type === PAGE_NEXT) setPageIndex(pageIndex + 1);
    if (type === PAGE_PRE) setPageIndex(pageIndex - 1);
    setLoading(false);
  };

  const onSortChange = (prop: string, sort: SortStates) => {
    setSortDirection(sort);
    setSortProp(sort ? prop : "");
    if (sortFetch) {
      pageRecordList();
    }
  };

  const onCheckAll = (e: any) => {
    const checked = e.target.checked;
    setIndeterminate(false);
    const data = [...tableData!];
    data.forEach((t) => {
      t["_isChecked"] = checked;
    });
    setTableData(data);
    checkChange && checkChange(checked ? tableData : [], tableData);
  };

  const queryRecordList = async (params: any, pageType: "NEXT" | "PREV") => {
    const { request, response } = interceptors;
    let handleParams = { ...params, sortProp, sortDirection };
    if (typeof request === "function") {
      handleParams = await request(handleParams);
    }
    /* eslint-disable */
    let { data: resp, err } = await axios.asyncAwait(fetchMethod, { params: { ...handleParams } });
    resp = Array.isArray(resp) ? { items: resp } : resp || { items: [] };
    if (err) return {};
    if (typeof response === "function") {
      resp = await response(resp);
    }
    const { items, hasPrev, hasNext, totalCount } = resp;
    const tList = Array.isArray(items) ? items : [];
    if (!pageType && tList.length <= 0 && params.page > 1) {
      pageRecordList(PAGE_PRE);
      return {};
    }
    if (hasPrev !== undefined && hasNext !== undefined) {
      setPageStatus({
        hasNext,
        hasPrev,
      });
    }
    if (totalCount !== undefined) {
      setTotal(totalCount);
    }
    const finalList = tList.map((t) => {
      t["_isExpanded"] = isExpandable ? false : undefined;
      t["_isChecked"] = isCheckable ? false : undefined;
      return t;
    });

    return {
      list: finalList,
      hasNext,
      hasPrev,
      total: totalCount,
    };
  };

  const handleRow = (item: RowNeedProps, col: ColumnType<ObjAny>) => {
    const { prop = "", isI18n, isUpperCase, formatter } = col;
    const val = item[prop];
    if (typeof formatter === "function") {
      return formatter(val, item, prop as string, col);
    }
    if (isI18n) {
      return t(val);
    }
    if (isUpperCase) {
      return val.toUpperCase();
    }
    return val;
  };

  const onRowClick = (item: RowNeedProps) => {
    const row = tableData.find((obj) => obj.id === item.id);
    if (isExpandable) {
      row!["_isExpanded"] = !item._isExpanded;
      setTableData([...tableData]);
    }
    if (isCheckable) {
      row!["_isChecked"] = !item._isChecked;
      const filterArr = tableData.filter((t) => {
        return t._isChecked;
      });

      const indeterminate = filterArr.length > 0 && filterArr.length < tableData.length;
      const isCheckAll = filterArr.length === tableData.length;
      setIndeterminate(indeterminate);
      setIsCheckAll(isCheckAll);
      checkChange && checkChange(filterArr, tableData);
    }
  };

  const getStyle = (col: ColumnType<ObjAny>, type: "header" | "body") => {
    const { flexWeight = 1, textAlign = "left", width = undefined, headerCellStyle, bodyCellstyle } = col;

    return {
      flex: flexWeight,
      width: width,
      "text-align": textAlign,
      ...(type === "header" ? headerCellStyle : bodyCellstyle),
    };
  };

  const onPrePage = async (cb: () => any) => {
    await pageRecordList(PAGE_PRE);
    cb();
  };

  const onNextPage = async (cb: (flag?: boolean) => any) => {
    const res = await pageRecordList(PAGE_NEXT);
    if (isPaginationById) {
      // cb(!!res || res.hasNext);
    }
    cb(!!res || tableData.length < pageSize);
  };

  // 固定表头body区域滚动 必须给定整个table-container的父容器高度或者给定table-view高度
  const fixLayout = () => {
    if (!isFixLayout) return;
    const tableContainerHeight = TableContainerRef.current?.offsetHeight ?? 0;
    const searchViewHeight = SearchViewRef.current?.offsetHeight ?? 0;
    const tableHeaderHeight = TableHeaderRef.current?.offsetHeight ?? 0;
    const PaneViewHeight = PaneViewRef.current?.offsetHeight ?? 0;
    let newTableContentHeight = tableContainerHeight - searchViewHeight - tableHeaderHeight - PaneViewHeight;
    if (isPagination) {
      newTableContentHeight -= 60;
    }
    if (isPaneTable) {
      newTableContentHeight -= 50;
    }
    if (newTableContentHeight < minHeight) {
      newTableContentHeight = minHeight;
    }
    setTableContentHeight(newTableContentHeight);
  };

  const renderRowItem = (item: RowNeedProps, i: number) => (
    <div
      key={item.id || i}
      className={cx("row-item", { "row-arrow": isRowArrow, "expand-arrow": isExpandable, "expand-arrow-down": isExpandable && !!item._isExpanded })}
    >
      <ul
        onClick={() => {
          onRowClick(item);
        }}
        style={{ backgroundColor: bodyRowBgColor, "--body-row-bgcolor": rowHoverColor } as StyleProps}
      >
        {isCheckable && <Checkbox checked={item._isChecked}></Checkbox>}
        {columns.map((col, colIndex) => (
          <li
            key={colIndex}
            style={{ ...{ color: bodyRowColor }, ...getStyle(col, "body") }}
            className={(showOverflowTip || col.showOverflowTip) && !col.render ? "text-ellipsis" : ""}
          >
            {col.render ? (
              col.render(item[col.prop! as string], item, i, OverflowTip)
            ) : showOverflowTip || col.showOverflowTip ? (
              <OverflowTip value={handleRow(item, col)} />
            ) : (
              handleRow(item, col)
            )}
          </li>
        ))}
      </ul>
      {isExpandable && item._isExpanded && expandContainer && expandContainer(item)}
    </div>
  );
  return (
    <div ref={TableContainerRef} className={cx(styles["xt-table-container"], styles[`xt-table-container-${theme}`], "xt-table", className)}>
      <div ref={SearchViewRef} className="search-view">
        {queryComponent}
      </div>
      <div className={cx("table-view", isPaneTable && "table-pane")}>
        {isPaneTable && (
          <div ref={PaneViewRef} className="pane-view">
            <div className="p-title">{t("user.details")}</div>
            <div className="p-tool">{panneComponent}</div>
          </div>
        )}
        <ul ref={TableHeaderRef} className={cx("table-header", isExpandable && "header-expand-arrow")} style={{ backgroundColor: headerRowBgColor }}>
          {isCheckable && <Checkbox checked={isCheckAll} indeterminate={indeterminate} onChange={onCheckAll}></Checkbox>}
          {columns.map((col, i) => (
            <li key={i} style={{ ...{ color: headerRowColor }, ...getStyle(col, "header") }}>
              {col.isSort ? (
                <Sort prop={col.prop! as string} isme={sortProp} onSort={onSortChange}>
                  {col.label}
                </Sort>
              ) : (
                col.label
              )}
            </li>
          ))}
        </ul>
        <XtPane
          className="table-content"
          loading={isShowLoading && loading}
          noData={isEmpty}
          noIcon={!isShowEmptyImg}
          minHeight={minHeight}
          overflowType={overflowType}
          style={tableContentStyle}
          isNeedLogin={isNeedLogin}
        >
          {virtual ? (
            <List split={false}>
              <VirtualList data={getTableData} itemKey={itemKey || "id"} itemHeight={50} height={420}>
                {(item, i) => renderRowItem(item, i)}
              </VirtualList>
            </List>
          ) : (
            getTableData.map((item, i) => renderRowItem(item, i))
          )}
        </XtPane>
        {isPagination && showPagination && (
          <XtPagination
            ref={paginationRef}
            className={cx({ "un-visible": !showPagination }, "list-pagination")}
            pageIndex={pageIndex}
            prepage={onPrePage}
            nextpage={onNextPage}
          />
        )}
      </div>
    </div>
  );
};
export default observer(forwardRef(Table), { forwardRef: true }) as React.FC<XtTableProps<any>>;
